import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import ShareModal from 'pages/ContributionView/components/ShareModal/ShareModalContainer';
import { shareViaEmail, determineDarkThemedColorToUse } from 'services/contributions.service';
import axiosInstance from 'utils/axiosInstance';
import useRouter from 'hooks/useRouter';
import { useAccount } from 'hooks';
import { addhttp } from 'utils/utils';
import ContributionViewMainContainer from './ContributionViewMainContainer';
import { ContributionType, UserRoles } from 'helpers/constants';
import Loader from 'components/UI/Loader';
import { isEmpty, isEqual } from 'lodash';
import { memo } from 'react';

function ContributionViewContainer(props) {
  const {
    match: {
      params: { id },
    },
  } = props;

  const [modalOpened, setModalOpened] = useState(false);
  const [emailsError, setEmailsError] = useState({
    isError: false,
    errorMessage: null,
  });
  const [loadingDomainName, setLoadingDomainName] = useState(true);
  const activeContribution = useSelector(state => state.contributions?.activeContribution);
  const { subdomain, parentDomain, pathname, history, query, location } = useRouter();
  const { currentRole } = useAccount();
  const clientPreviewMode = location?.pathname?.includes('clientpreview');
  const isCoach = currentRole === UserRoles.cohealer;
  const isDarkThemeEnabled = determineDarkThemedColorToUse(activeContribution);

  const handleModalClose = useCallback(() => {
    setEmailsError({
      isError: false,
      errorMessage: null,
    });
    setModalOpened(false);
  }, [setModalOpened]);

  const handleShareButtonClick = useCallback(e => {
    e.preventDefault();
    setModalOpened(true);
  }, []);

  const getApiData = useCallback(
    () => axiosInstance.get(`/ProfilePage/GetProfileLinkNameByContributionId?ContributionId=${id}`),
    [id],
  );

  useEffect(() => {
    if (subdomain) {
      setLoadingDomainName(false);
      return;
    }
    getApiData()
      .then(res => {
        if (res.data) {
          if (query?.isPurchased) {
            window.location.replace(addhttp(`${res.data}.${parentDomain}${pathname}?isPurchased=true`));
          } else {
            window.location.replace(
              addhttp(`${res.data}.${parentDomain}${pathname.concat(location.search).concat(location.hash)}`),
            );
          }
        } else {
          setLoadingDomainName(false);
        }
      })
      .catch(() => {
        setLoadingDomainName(false);
      });
  }, [id]);

  const handleModalSubmit = useCallback(
    (emails, priceId) => {
      shareViaEmail(
        priceId === null
          ? { contributionId: id, emailAddresses: emails }
          : { contributionId: id, emailAddresses: emails, priceId: priceId },
      )
        .then(() => {
          handleModalClose();
        })
        .catch(err => {
          setEmailsError({
            isError: true,
            errorMessage: err?.response?.data?.message,
          });
        });
    },
    [handleModalClose, id],
  );

  useEffect(() => {
    if (
      !isEmpty(activeContribution) &&
      (!activeContribution?.applicationFormViewModel || activeContribution?.applicationFormViewModel?.isDeleted) &&
      pathname.includes('apply-contribution-view')
    ) {
      const newPath = pathname.replace('apply-contribution-view', 'contribution-view');
      history.replace(newPath);
    }
  }, [activeContribution]);

  if (loadingDomainName) {
    return <Loader />;
  }

  return (
    <>
      <ContributionViewMainContainer handleShareButtonClick={handleShareButtonClick}>
        {modalOpened && (
          <ShareModal
            isOpen={modalOpened}
            onSubmit={handleModalSubmit}
            onModalClose={handleModalClose}
            emailsError={emailsError}
            setEmailsError={setEmailsError}
            contributionId={id}
            paymentInfo={activeContribution?.paymentInfo}
            isInvoiced={activeContribution?.isInvoiced}
            symbol={activeContribution?.defaultSymbol}
            type={activeContribution?.type}
          />
        )}
      </ContributionViewMainContainer>
      {(((pathname?.toLowerCase()?.includes('about') ||
        (activeContribution?.type === ContributionType.contributionOneToOne &&
          pathname?.toLowerCase()?.includes('sessions'))) &&
        !isCoach &&
        !activeContribution?.isPurchased) ||
        clientPreviewMode) &&
        activeContribution?.isFooterEnabled &&
        activeContribution?.privacyPolicyUrl && (
          <p
            className="privacy-policy-link"
            style={{
              cursor: 'pointer',
              fontSize: '16px',
              color: isDarkThemeEnabled ? 'white' : 'rgba(74, 74, 74, 1)',
              textAlign: 'center',
              backgroundColor: isDarkThemeEnabled ? '#252728' : 'white',
              padding: '10px',
            }}
            onClick={() => window.open(activeContribution?.privacyPolicyUrl, '_blank')}
          >
            Privacy Policy
          </p>
        )}
    </>
  );
}

ContributionViewContainer.propTypes = {
  match: PropTypes.shape({
    path: PropTypes.string,
    url: PropTypes.string,
    isExact: PropTypes.bool,
    params: PropTypes.object,
  }).isRequired,
};

export default ContributionViewContainer;
// export default memo(ContributionViewContainer, (prev, next) => isEqual(prev, next));
